import { Eye, EyeOff, HelpCircle } from "lucide-react"
import { createContext, type ReactNode, useContext, useMemo } from "react"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import type { Scope } from "@/lib/connection"
import type { Artifact } from "@/lib/defs"
import { useT } from "@/lib/i18n"
import { useDefinitions, usePrincipalRecord } from "@/lib/queries"
import { useSession } from "@/lib/session"
import { asArray, asString, cn, isRecord } from "@/lib/utils"

/**
 * Department lenses. The cell masks marked fields per principal; this module only explains the
 * outcome from installed definitions (marking catalogs and `norn.reveal` rules on `norn.Marking`).
 * Nothing here decides visibility: a hidden value never reaches the browser.
 */

export interface MarkingInfo {
  readonly id: string
  readonly label: string
  readonly severity: string | undefined
}

export interface RevealRule {
  readonly policyId: string
  readonly policyName: string
  readonly ruleId: string
  readonly roles: readonly string[]
  readonly markings: readonly string[]
}

/** Every marking an installed catalog declares, by id. */
export function markingCatalog(artifacts: readonly Artifact[]): Map<string, MarkingInfo> {
  const out = new Map<string, MarkingInfo>()
  for (const a of artifacts) {
    if (a.kind !== "entity") continue
    for (const m of asArray(a.payload.markings)) {
      if (!isRecord(m) || typeof m.id !== "string") continue
      out.set(m.id, { id: m.id, label: asString(m.label) ?? m.id, severity: asString(m.severity) })
    }
  }
  return out
}

/** Installed rules that permit `norn.reveal` of named markings, with the roles they require. */
export function revealRules(artifacts: readonly Artifact[]): RevealRule[] {
  const out: RevealRule[] = []
  for (const a of artifacts) {
    if (a.kind !== "policy") continue
    const policyId = asString(a.payload.policyId) ?? a.artifactId
    for (const rule of asArray(a.payload.rules)) {
      if (!isRecord(rule) || rule.resourceType !== "norn.Marking" || !asArray(rule.actions).includes("norn.reveal")) continue
      const roles: string[] = []
      const markings: string[] = []
      for (const condition of asArray(rule.allOf)) {
        if (!isRecord(condition)) continue
        if (isRecord(condition.role)) roles.push(...asArray(condition.role.anyOf).filter((r): r is string => typeof r === "string"))
        if (isRecord(condition.marking)) markings.push(...asArray(condition.marking.anyOf).filter((m): m is string => typeof m === "string"))
      }
      if (markings.length) out.push({ policyId, policyName: a.name ?? policyId, ruleId: asString(rule.ruleId) ?? "", roles, markings })
    }
  }
  return out
}

/** Department roles that installed reveal rules name, in a stable reading order. */
export function lensRoles(rules: readonly RevealRule[]): string[] {
  const roles = new Set(rules.flatMap((r) => r.roles))
  return [...roles].sort((a, b) => Number(a === "Manager") - Number(b === "Manager") || a.localeCompare(b))
}

/** Whether a principal with `roles` would be admitted by some reveal rule for `marking` (explanation only). */
export function rolesReveal(rules: readonly RevealRule[], roles: readonly string[], marking: string): boolean {
  return rules.some((r) => r.markings.includes(marking) && (r.roles.length === 0 || r.roles.some((role) => roles.includes(role))))
}

export interface LensContextValue {
  readonly catalog: Map<string, MarkingInfo>
  readonly rules: readonly RevealRule[]
  readonly roles: readonly string[] | null
  readonly actor: string | null
}

const LensContext = createContext<LensContextValue | null>(null)

export function LensProvider({ scope, children }: { scope: Scope; children: ReactNode }) {
  const defs = useDefinitions(scope)
  const principal = usePrincipalRecord(scope)
  const value = useMemo<LensContextValue>(() => {
    const artifacts = defs.data?.artifacts ?? []
    return {
      catalog: markingCatalog(artifacts),
      rules: revealRules(artifacts),
      roles: principal.data ? principal.data.roles : null,
      actor: principal.data ? principal.data.actor : null,
    }
  }, [defs.data, principal.data])
  return <LensContext.Provider value={value}>{children}</LensContext.Provider>
}

export function useLens(): LensContextValue | null {
  return useContext(LensContext)
}

/** The lens a principal reads through: its department roles, else its actor. */
export function lensLabel(lens: LensContextValue | null): string | null {
  if (!lens?.roles) return null
  const departments = lens.roles.filter((r) => r !== "Restricted")
  return departments.length ? departments.join(" · ") : (lens.roles[0] ?? lens.actor)
}

/** One plain sentence per hidden marking: what it is, which rule reveals it, to whom. */
export function explainHidden(lens: LensContextValue, markings: readonly string[]) {
  return markings.map((id) => {
    const info = lens.catalog.get(id)
    const grants = lens.rules.filter((r) => r.markings.includes(id))
    return { id, label: info?.label ?? id, grants }
  })
}

/** "Hidden for your role", with the reason one click away. Values withheld by the cell are never sent. */
export function HiddenForRole({ markings, className, compact = false }: { markings: readonly string[]; className?: string; compact?: boolean }) {
  const t = useT()
  const lens = useLens()
  const chip = (
    <span
      className={cn(
        "inline-flex h-[22px] items-center gap-1.5 rounded-full border border-dashed border-border bg-surface-subtle/40 px-2 text-xs text-muted-foreground",
        className,
      )}
    >
      <EyeOff className="size-3" aria-hidden />
      {compact ? t("lens.hidden") : t("lens.hiddenForRole")}
    </span>
  )
  if (!lens || markings.length === 0) return chip
  const reasons = explainHidden(lens, markings)
  return (
    <Popover>
      <PopoverTrigger
        aria-label={t("lens.whyHidden")}
        className="group inline-flex items-center gap-1 rounded-full outline-none focus-visible:ring-3 focus-visible:ring-ring/30"
      >
        {chip}
        {!compact ? <HelpCircle className="size-3.5 text-subtle-foreground group-hover:text-foreground" aria-hidden /> : null}
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[22rem] gap-3 rounded-2xl p-4">
        <p className="text-sm font-medium">{t("lens.whyHidden")}</p>
        <ul className="space-y-3">
          {reasons.map((r) => (
            <li key={r.id} className="space-y-1 text-[13px]">
              <p>
                {t("lens.markedAs", { label: r.label })} <code dir="ltr" className="font-mono text-2xs text-muted-foreground">{r.id}</code>
              </p>
              {r.grants.length ? (
                r.grants.map((g) => (
                  <p key={`${g.policyId}/${g.ruleId}`} className="text-muted-foreground">
                    {t("lens.revealedBy", { roles: g.roles.join(", ") || t("lens.anyRole"), policy: g.policyName })}{" "}
                    <code dir="ltr" className="font-mono text-2xs">{g.ruleId}</code>
                  </p>
                ))
              ) : (
                <p className="text-muted-foreground">{t("lens.noReveal")}</p>
              )}
            </li>
          ))}
        </ul>
        <p className="text-xs text-muted-foreground">{t("lens.whoGrants")}</p>
        <p className="text-2xs text-subtle-foreground">{t("lens.serverSide")}</p>
      </PopoverContent>
    </Popover>
  )
}

const DEMO_TOKEN = /^demo-([a-z]+)$/

/** Showcase personas are public selectors (`demo-<actor>`); real sign-ins show their role, read-only. */
export function LensSwitcher({ className }: { className?: string }) {
  const t = useT()
  const lens = useLens()
  const session = useSession()
  const label = lensLabel(lens)
  const departments = lens ? lensRoles(lens.rules).filter((r) => r !== "Manager") : []
  const current = session.connection?.token?.match(DEMO_TOKEN)?.[1] ?? null
  const switchable = session.auth.kind === "dev" && current !== null && departments.length > 0 && session.connection !== null
  if (!label && !switchable) return null
  if (!switchable) {
    return (
      <p className={cn("inline-flex items-center gap-1.5 text-xs text-muted-foreground", className)}>
        <Eye className="size-3.5" aria-hidden />
        {t("lens.viewingAs", { lens: label ?? "" })}
      </p>
    )
  }
  const personas = ["manager", ...departments.map((d) => d.toLowerCase()), "restricted"]
  const connection = session.connection
  return (
    <div role="group" aria-label={t("lens.switch")} className={cn("flex flex-wrap items-center gap-2", className)}>
      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
        <Eye className="size-3.5" aria-hidden />
        {t("lens.viewAs")}
      </span>
      <div className="flex flex-wrap gap-0.5 rounded-full border border-border p-0.5">
        {personas.map((p) => {
          const name = p === "hr" ? "HR" : p.charAt(0).toUpperCase() + p.slice(1)
          const active = p === current
          return (
            <button
              key={p}
              type="button"
              aria-pressed={active}
              onClick={() => (active || !connection ? undefined : session.connect({ ...connection, token: `demo-${p}` }))}
              className={cn(
                "h-7 rounded-full px-3 text-xs font-medium outline-none transition-colors focus-visible:ring-3 focus-visible:ring-ring/30",
                active ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground",
              )}
            >
              {name}
            </button>
          )
        })}
      </div>
    </div>
  )
}
